import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Users, Trophy, Clock, Star, Briefcase, CalendarCheck } from 'lucide-react'
import ScrollArrow from './ScrollArrow'
import { mockStatsData } from '../data/mockData'
import { API_CONFIG, getApiHeaders } from '../config/api'

const Statistics: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const [counts, setCounts] = useState({
    members: 0,
    events: 0,
    hours: 0,
    achievements: 0,
    territories: 0,
    alliances: 0,
  })

  const [apiData, setApiData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [lastFetchTime, setLastFetchTime] = useState<number | null>(null)

  // Sparkles configuration (same as Hero) - вынесено за пределы состояния
  const NUM_SPARKLES = window.innerWidth <= 768 ? 20 : 35;
  const getRandom = (min: number, max: number) => Math.random() * (max - min) + min;
  
  const sparkleColors = [
    'bg-primary-400', // оранжевый
    'bg-pink-400',    // розовый
    'bg-purple-400',  // фиолетовый
    'bg-blue-400',    // синий
    'bg-cyan-400',    // голубой
    'bg-white',       // белый
    'bg-primary-300', // светло-оранжевый
    'bg-pink-300',    // светло-розовый
  ];

  // Создаем статичные sparkles один раз
  const staticSparkles = React.useMemo(() => {
    return Array.from({ length: NUM_SPARKLES }).map((_, i) => {
      const left = getRandom(5, 95);
      const top = getRandom(5, 95);
      const xMove = getRandom(-120, 120);
      const yMove = getRandom(-120, 120);
      const duration = getRandom(14, 28);
      const delay = getRandom(0, 4);
      const size = getRandom(0.5, 2.5);
      const opacity = getRandom(0.15, 0.35);
      const color = sparkleColors[Math.floor(Math.random() * sparkleColors.length)];
      
      return {
        id: i,
        left,
        top,
        xMove,
        yMove,
        duration,
        delay,
        size,
        opacity,
        color
      };
    });
  }, []); // Пустой массив зависимостей - создается только один раз

  // Fetch API data with caching
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.STATS}`, {
          method: 'GET',
          headers: getApiHeaders()
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        
        const data = await response.json()

        setApiData(data)
        setLastFetchTime(Date.now())
        setLoading(false)
      } catch (error) {
        console.error('Error fetching stats:', error)

        setApiData(mockStatsData)
        setLastFetchTime(Date.now())
        setLoading(false)
      }
    }

    const shouldFetchData = () => {
      // Если данных нет, загружаем
      if (!apiData) return true
      
      // Если время последнего запроса не установлено, загружаем
      if (!lastFetchTime) return true
      
      // Проверяем, прошло ли 30 минут (30 * 60 * 1000 = 1,800,000 мс)
      const thirtyMinutes = 30 * 60 * 1000
      const timeSinceLastFetch = Date.now() - lastFetchTime
      
      return timeSinceLastFetch > thirtyMinutes
    }

    if (shouldFetchData()) {
      fetchStats()
    } else {
      // Если данные актуальны, просто убираем loading
      setLoading(false)
    }
  }, [apiData, lastFetchTime])

  const stats: Array<{
    icon: React.ComponentType<{ className?: string }>
    value: number
    label: string
    suffix: string
    color: string
    format?: (value: number) => string
  }> = [
    {
      icon: Users,
      value: apiData?.users?.active_last_30_days|| 90,
      label: 'Активных участников',
      suffix: '',
      color: 'text-blue-400',
    },
    {
      icon: Trophy,
      value: 95, // Захардкожено как 200+
      label: 'Внутренних евентов',
      suffix: '+',
      color: 'text-yellow-400',
    },
    {
      icon: Clock,
      value: Math.floor(apiData?.voice?.total_hours || 0), // Целочисленное значение
      label: 'Часов сыграно вместе',
      suffix: '',
      color: 'text-green-400',
    },
    {
      icon: Star,
      value: apiData?.contracts?.total_completed || 0,
      label: 'Выполнено контрактов',
      suffix: '',
      color: 'text-purple-400',
    },
    {
      icon: Briefcase,
      value: apiData?.payments?.total_amount || 0,
      label: 'Выплачено премий',
      suffix: '',
      color: 'text-indigo-400',
      format: (value: number) => `$${(value / 1000000).toFixed(1)}M`,
    },
    {
      icon: CalendarCheck,
      value: apiData?.daily_tasks?.total_completed || 0,
      label: 'Дейликов выполнено',
      suffix: '',
      color: 'text-indigo-400',
    },
  ]



  useEffect(() => {
    if (inView && !loading && apiData) {
      const duration = 2000
      const steps = 60
      const stepDuration = duration / steps

      const timer = setInterval(() => {
        setCounts(prev => {
          const newCounts = { ...prev }
          let allComplete = true

          stats.forEach((stat, index) => {
            const key = Object.keys(counts)[index] as keyof typeof counts
            const target = stat.value
            const current = prev[key]
            const increment = target / steps

            if (current < target) {
              newCounts[key] = Math.min(current + increment, target)
              allComplete = false
            }
          })

          if (allComplete) {
            clearInterval(timer)
          }

          return newCounts
        })
      }, stepDuration)

      return () => clearInterval(timer)
    }
  }, [inView, loading, apiData])

  return (
    <section id="statistics" className="min-h-screen flex items-center bg-dark-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-72 h-72 bg-primary-600/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary-400/10 rounded-full blur-3xl"></div>
        
        {/* Floating Sparkles (same as Hero) */}
        <div className="absolute inset-0 overflow-hidden">
          {staticSparkles.map((sparkle) => (
            <motion.div
              key={sparkle.id}
              className={`absolute ${sparkle.color} rounded-full blur-md drop-shadow-lg`}
              style={{
                left: `${sparkle.left}%`,
                top: `${sparkle.top}%`,
                width: `${sparkle.size * 0.5}rem`,
                height: `${sparkle.size * 0.5}rem`,
                opacity: sparkle.opacity,
                filter: `blur(2px) drop-shadow(0 0 8px currentColor)`,
              }}
              animate={{
                x: [0, sparkle.xMove, 0],
                y: [0, sparkle.yMove, 0],
              }}
              transition={{
                duration: sparkle.duration,
                repeat: Infinity,
                delay: sparkle.delay,
              }}
            />
          ))}
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >

          
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-4xl md:text-5xl font-display font-bold text-white mb-6"
          >
            Цифры, которые говорят {' '}
            <span className="gradient-text">сами за себя</span>
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="text-xl text-gray-300 max-w-3xl mx-auto"
          >
          
          </motion.p>
        </motion.div>

        {/* Statistics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {loading ? (
            // Loading state
            Array.from({ length: 6 }).map((_, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.5 + index * 0.1, duration: 0.5 }}
                className="text-center group"
              >
                <div className="card hover:border-primary-500/50 transition-all duration-300 group-hover:transform group-hover:scale-105 p-4">
                  <div className="w-12 h-12 bg-dark-700 rounded-full flex items-center justify-center mx-auto mb-3 animate-pulse">
                    <div className="w-6 h-6 bg-gray-600 rounded"></div>
                  </div>
                  
                  <div className="text-2xl md:text-3xl font-bold text-white mb-2 animate-pulse">
                    <div className="w-16 h-8 bg-gray-600 rounded mx-auto"></div>
                  </div>
                  
                  <div className="text-gray-300 text-xs font-medium animate-pulse">
                    <div className="w-20 h-4 bg-gray-600 rounded mx-auto"></div>
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            // Actual stats
            stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.5 + index * 0.1, duration: 0.5 }}
                className="text-center group"
              >
                <div className="card hover:border-primary-500/50 transition-all duration-300 group-hover:transform group-hover:scale-105 p-4">
                  <div className={`w-12 h-12 bg-dark-700 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-primary-600/20 transition-colors`}>
                    <stat.icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                  
                  <div className="text-2xl md:text-3xl font-bold text-white mb-2">
                    {stat.format ? stat.format(Math.floor(counts[Object.keys(counts)[index] as keyof typeof counts])) : Math.floor(counts[Object.keys(counts)[index] as keyof typeof counts])}
                    {!stat.format && <span className="text-primary-400">{stat.suffix}</span>}
                  </div>
                  
                  <div className="text-gray-300 text-xs font-medium">
                    {stat.label}
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-8 text-center"
        >
          <div className="p-6">
            <h3 className="text-xl font-display font-bold text-white mb-3">
              Обновление в реальном времени
            </h3>
            <p className="text-gray-300 max-w-2xl mx-auto text-sm">
            Наша статистика обновляется в режиме реального времени с помощью API, 
            поэтому вы всегда будете получать самую актуальную информацию о достижениях и деятельности нашей семьи.
            </p>

          </div>
        </motion.div>
      </div>

      {/* Scroll Arrow */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ delay: 1.5, duration: 0.5 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 hidden sm:block"
      >
        <ScrollArrow targetId="team" />
      </motion.div>
    </section>
  )
}

export default Statistics 